import React, { useContext, useState, useEffect, useMemo } from 'react';
import {
	isProductIdWhiteListed,
	JiraProduct,
	JiraProductDiscovery,
	JiraServiceManagementProduct,
	JiraSoftwareProduct,
	JiraWorkManagementProduct,
	makeAri,
} from '../../utils';
import {
	mapProductsToProductSelectOptions,
	mapProductSelectOptionsToProducts,
	mapInviteProductsToProducts,
	getInitialSelectedProducts,
} from './ProductSelectUtils';
import {
	CollectionType,
	type Collections,
	type InviteCapabilitiesObject,
	type InviteProduct,
	type Product,
	type ProductSelectOption,
} from '../../types';
import { defaultInviteApiClient } from '../../clients';
import { useInvitePeopleContext } from '../InvitePeople';
import { ProductSelectAnalytics } from './ProductSelectAnalytics';
import { useAnalytics } from '../../analytics';
import { invitePeopleRendered, invitePeopleRenderedTimings } from '../../analytics/ufoExperiences';
import { getTwcProducts } from '../../utils/twcForCoreInvites/getTwcProducts';
import { fg } from '@atlaskit/platform-feature-flags';
import { FetchError } from '../../clients/helpers';

type ProductList = Product[];

type ProductsState = {
	availableProductOptions: ProductSelectOption[];
	availableProducts: ProductList;
	hasPermissionToInviteOrRequestAccess: boolean;
	inviteCapabilities: InviteCapabilitiesObject;
	isFetchProductsLoading: boolean;
	selectedProducts: string[];
	hasPreselectedTwc: boolean;
	setAvailableProducts: (products: ProductList) => void;
	setSelectedProducts: (products: string[]) => void;
};

export type ProductsContextProps = {
	resourceAri: string;
	productOptions: ProductSelectOption[] | undefined;
	children: React.ReactNode;
};
export const ProductsStateContext = React.createContext<ProductsState | undefined>(undefined);

export type ProductSelectPerformanceMarkings = typeof PERFORMANCE_MARKINGS;
const PERFORMANCE_MARKINGS = {
	getInviteCapabilitiesStart: (): void =>
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getInviteCapabilities.startMark),
	getInviteCapabilitiesEnd: (): void =>
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getInviteCapabilities.endMark),
} as const;

/**
 * @function getAvailableProducts fetches invite-capabilities and uses the capabilities to calculate the products a user can invite to
 * replaces getAvailableProducts after puma-540-expose-invite-capabilities is rolled out
 */
export const _getAvailableProducts = async ({
	cloudId,
	resourceAri,
	onSuccess,
	onFail,
	performance,
}: {
	cloudId: string;
	resourceAri: string;
	performance: ProductSelectPerformanceMarkings;
	onSuccess: (resourceAri: string, availableResourcesAri: string[]) => void;
	onFail: (resourceAri: string, errMsg?: string, statusCode?: number) => void;
}): Promise<{
	productOptions: Product[];
	inviteCapabilities: InviteCapabilitiesObject;
}> => {
	try {
		performance.getInviteCapabilitiesStart();
		const response = await defaultInviteApiClient.inviteCapabilities(cloudId as string, true);
		performance.getInviteCapabilitiesEnd();

		const products = calculateProductsFromInviteCapabilities(response);
		if (!products?.length) {
			onSuccess(resourceAri, []);
			return {
				productOptions: [],
				inviteCapabilities: { directInvite: [], invitePendingApproval: [] },
			};
		}

		onSuccess(
			resourceAri,
			products.map(({ id }) => `ari:cloud:${id}::site/${cloudId}`),
		);

		return {
			productOptions: products,
			inviteCapabilities: response,
		};
	} catch (err) {
		const errMsg = err instanceof Error ? err.message : '';
		const statusCode = err instanceof FetchError ? err.responseCode : undefined;

		onFail(resourceAri, errMsg, statusCode);
		return {
			productOptions: [],
			inviteCapabilities: { directInvite: [], invitePendingApproval: [] },
		};
	}
};

function uniqueById<T extends { id: string }>(arr: T[]): T[] {
	return [...new Map<string, T>(arr.map((item) => [item.id, item])).values()];
}

export function calculateHasPermissionToInviteOrRequestAccess({
	cloudId,
	productId,
	jiraSubProduct,
	inviteCapabilities,
	productIdWhitelist,
}: {
	cloudId: string | undefined;
	productId: string;
	jiraSubProduct: string | undefined;
	inviteCapabilities: InviteCapabilitiesObject;
	productIdWhitelist?: string[];
}) {
	if (!productId || !cloudId) {
		return false;
	}
	const { directInvite, invitePendingApproval } = inviteCapabilities;
	const capabilities = [...directInvite, ...invitePendingApproval];
	if (productIdWhitelist) {
		const whitelist = new Set(productIdWhitelist);
		return capabilities.some((p) => whitelist.has(p.id));
	}
	if (productId === 'platform') {
		return directInvite.length > 0 || invitePendingApproval.length > 0;
	}
	const currentProductId = jiraSubProduct || productId;
	const currentProductAri = makeAri(currentProductId, cloudId);
	const hasRelatedProduct = (config: InviteProduct) => {
		if (productId === JiraProduct.id) {
			if (!jiraSubProduct || jiraSubProduct === 'unknown') {
				return (
					config.ari.indexOf(JiraWorkManagementProduct.id) > 0 ||
					config.ari.indexOf(JiraServiceManagementProduct.id) > 0 ||
					config.ari.indexOf(JiraSoftwareProduct.id) > 0 ||
					config.ari.indexOf(JiraProductDiscovery.id) > 0
				);
			} else if (jiraSubProduct === JiraWorkManagementProduct.id) {
				return (
					config.ari.indexOf(JiraWorkManagementProduct.id) > 0 ||
					config.ari.indexOf(JiraSoftwareProduct.id) > 0
				);
			}
		}
		return config.ari === currentProductAri;
	};
	return capabilities.some(hasRelatedProduct);
}

function calculateProductsFromInviteCapabilities(
	inviteCapabilities: InviteCapabilitiesObject,
): Product[] {
	const uniqueInviteProducts = uniqueById([
		...inviteCapabilities.directInvite,
		...inviteCapabilities.invitePendingApproval,
	]);

	return mapInviteProductsToProducts(uniqueInviteProducts).filter((p: Product) =>
		isProductIdWhiteListed(p.id),
	);
}

/**
 * @deprecated
 * will be deleted when puma-540-expose-invite-capabilities is cleaned up
 * replaced by the newer _getAvailableProducts
 **/
export const getAvailableProducts = async (
	cloudId: string,
	resourceAri: string,
	analytics: ProductSelectAnalytics,
): Promise<{
	productOptions: ProductList;
	inviteCapabilities: InviteCapabilitiesObject;
}> => {
	try {
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getInviteCapabilities.startMark);
		const response = await defaultInviteApiClient.inviteCapabilities(cloudId as string);
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getInviteCapabilities.endMark);

		// Get unique capabilities.
		const resources = [
			// Create map (matching keys override) and get just the values
			...new Map(
				[...response.directInvite, ...response.invitePendingApproval].map((item) => [
					item.id,
					item,
				]),
			).values(),
		];
		analytics.getAvailableProductsSucceeded(
			resourceAri,
			resources.map(({ id }) => `ari:cloud:${id}::site/${cloudId}`),
		);
		if (!resources?.length) {
			return {
				productOptions: [],
				inviteCapabilities: { directInvite: [], invitePendingApproval: [] },
			};
		}

		const resourcesAsOptions = mapInviteProductsToProducts(resources).filter((p) =>
			isProductIdWhiteListed(p.id),
		);

		return {
			productOptions: resourcesAsOptions,
			inviteCapabilities: response,
		};
	} catch (err) {
		analytics.getAvailableProductsFailed(resourceAri, err);
		return {
			productOptions: [],
			inviteCapabilities: { directInvite: [], invitePendingApproval: [] },
		};
	}
};

export const getCollections = async (
	cloudId: string | undefined,
	resourceAri: string,
	analytics: ProductSelectAnalytics,
): Promise<Collections> => {
	if (!cloudId) {
		return {};
	}
	try {
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getCollections.startMark);
		const response = await defaultInviteApiClient.getCollections({ cloudId });
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getCollections.endMark);
		analytics.getCollectionsSucceeded(
			resourceAri,
			!!response?.[CollectionType.TEAMWORK_COLLECTION]?.isEnabled,
		);
		return response;
	} catch {
		analytics.getCollectionsFailed(resourceAri);
		return {};
	}
};

export const ProductContext: React.FC<ProductsContextProps> = ({
	resourceAri,
	children,
	productOptions,
}: ProductsContextProps) => {
	const analytics = useAnalytics(ProductSelectAnalytics);
	const { cloudId, jiraSubProduct, productId } = useInvitePeopleContext();

	const productOptionsFiltered = productOptions
		? mapProductSelectOptionsToProducts(productOptions).filter((p: Product) =>
				isProductIdWhiteListed(p.id),
			)
		: [];

	const [collections, setCollections] = useState<Collections>({});
	const [availableProducts, setAvailableProducts] = useState<ProductList>(productOptionsFiltered);
	const [selectedProducts, setSelectedProducts] = useState<string[]>(
		getInitialSelectedProducts({
			productOptions: availableProducts,
			productId,
			jiraSubProduct,
			collections,
		}),
	);
	const [inviteCapabilities, setInviteCapabilities] = useState<InviteCapabilitiesObject>({
		directInvite: [],
		invitePendingApproval: [],
	});

	const hasPreselectedTwc = useMemo(
		() =>
			getTwcProducts(availableProducts, productId, jiraSubProduct, collections).isTwcPreselected,
		[availableProducts, productId, jiraSubProduct, collections],
	);

	const [isFetchProductsLoading, setIsFetchProductsLoading] = useState(true);
	useEffect(() => {
		const fetchProductData = async () => {
			if (productOptionsFiltered.length === 0 && isFetchProductsLoading) {
				let availableProductsPromise: Promise<{
					inviteCapabilities: InviteCapabilitiesObject;
					productOptions: Product[];
				}>;

				if (fg('puma-540-expose-invite-capabilities')) {
					availableProductsPromise = _getAvailableProducts({
						cloudId: cloudId ?? '',
						resourceAri,
						onSuccess: analytics.getAvailableProductsSucceeded.bind(analytics),
						onFail: analytics._getAvailableProductsFailed.bind(analytics),
						performance: PERFORMANCE_MARKINGS,
					});
				} else {
					availableProductsPromise = getAvailableProducts(cloudId || '', resourceAri, analytics);
				}

				const collectionsPromise = fg('puma-1273-productionise-twc-for-core-invites')
					? getCollections(cloudId, resourceAri, analytics)
					: undefined;
				const [availableProducts, collectionsResponse] = await Promise.all([
					availableProductsPromise,
					collectionsPromise,
				]);
				const { productOptions, inviteCapabilities } = availableProducts;
				setAvailableProducts(productOptions);
				setInviteCapabilities(inviteCapabilities);

				const collections = collectionsResponse || {};
				setCollections(collections);

				setSelectedProducts(
					getInitialSelectedProducts({ productOptions, productId, jiraSubProduct, collections }),
				);
			}

			setIsFetchProductsLoading(false);
		};
		fetchProductData();
	}, [
		cloudId,
		productId,
		jiraSubProduct,
		resourceAri,
		isFetchProductsLoading,
		productOptionsFiltered.length,
		analytics,
	]);

	const availableProductOptions = useMemo(
		() =>
			mapProductsToProductSelectOptions(availableProducts, productId, jiraSubProduct, collections),
		[availableProducts, productId, jiraSubProduct, collections],
	);

	const hasPermissionToInviteOrRequestAccess = useMemo((): boolean => {
		if (!productId || !cloudId || isFetchProductsLoading) {
			return false;
		}
		const { directInvite, invitePendingApproval } = inviteCapabilities;
		if (productId === 'platform') {
			return directInvite.length > 0 || invitePendingApproval.length > 0;
		}
		const currentProductId = jiraSubProduct || productId;
		const currentProductAri = makeAri(currentProductId, cloudId);
		const hasRelatedProduct = (config: InviteProduct) => {
			if (productId === JiraProduct.id) {
				if (!jiraSubProduct || jiraSubProduct === 'unknown') {
					return (
						config.ari.indexOf(JiraWorkManagementProduct.id) > 0 ||
						config.ari.indexOf(JiraServiceManagementProduct.id) > 0 ||
						config.ari.indexOf(JiraSoftwareProduct.id) > 0 ||
						config.ari.indexOf(JiraProductDiscovery.id) > 0
					);
				} else if (jiraSubProduct === JiraWorkManagementProduct.id) {
					return (
						config.ari.indexOf(JiraWorkManagementProduct.id) > 0 ||
						config.ari.indexOf(JiraSoftwareProduct.id) > 0
					);
				}
			}
			return config.ari === currentProductAri;
		};
		return directInvite.some(hasRelatedProduct) || invitePendingApproval.some(hasRelatedProduct);
	}, [cloudId, jiraSubProduct, inviteCapabilities, isFetchProductsLoading, productId]);

	return (
		<ProductsStateContext.Provider
			value={{
				selectedProducts,
				availableProducts,
				setAvailableProducts,
				availableProductOptions,
				setSelectedProducts,
				isFetchProductsLoading,
				inviteCapabilities,
				hasPermissionToInviteOrRequestAccess,
				hasPreselectedTwc,
			}}
		>
			{children}
		</ProductsStateContext.Provider>
	);
};

/**
 * Hook that fetches available products and maintains state and logic for which products are currently selected
 */
export const useProducts = () => {
	const state = useContext(ProductsStateContext);

	if (state === undefined) {
		throw new Error('useProducts must be used within a ProductsStateContextProvider');
	}

	return state;
};
